import { useGetFastVarsQuery } from "@api/fast-versioning";
import type { StoryType } from "@config";
import { useAppSelector } from "@store";
import { getAccount } from "@store/account/selectors";
import { useCallback, useRef } from "react";
import {
  collectStoryFastVersionVars,
  type FsVarPoolEntry,
} from "./collect-story-fast-version-vars";

export {
  getCurrentStoryVariables,
  getStoryCalcVariables,
} from "./collect-story-fast-version-vars";

// Thin wrapper over `collectStoryFastVersionVars`, which holds the actual
// composition logic and is shared with the AI agent. This hook's only jobs are
// to supply the org's backend variables (the one piece of state a hook can
// reach) and to hand back the JSON string publish writes into
// `settings.fs["fs-vars"]`.
export const useGetStoryFastVersionVars = () => {
  "use no memo";
  const orgInfo = useAppSelector(getAccount);
  const { plan } = orgInfo || { plan: undefined };
  const isTrialPlan = plan === "trial";
  const { data: variables = [] } = useGetFastVarsQuery("fast-vars", {
    skip: isTrialPlan,
  });

  // Use a ref so the callback always reads the latest value,
  // even when captured by stale closures (e.g. useCallback with [] deps)
  const variablesRef = useRef(variables);
  variablesRef.current = variables;

  const handleVariables = useCallback(
    ({ story }: { story: StoryType }): string => {
      if (!story || !story.settings) return "[]";

      return (
        JSON.stringify(
          collectStoryFastVersionVars({
            // Callers pass a story whose `slides` already carry the live
            // content (use-publish inverts `flatContentLayers` first).
            slides: story.slides,
            settings: story.settings,
            backendVars: variablesRef.current as FsVarPoolEntry[],
          }),
        ) || "[]"
      );
    },
    [],
  );

  return handleVariables;
};
